"use client";

import { cn } from "@/lib/utils";
import CustomButton from "../ui/custom-button";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Phone, MapPin, Clock } from "lucide-react";
import { CONTACT, formatUsPhone } from "@/lib/constants/contact";
import { SECTION_CONTAINER, SECTION_PADDING } from "@/lib/home-section-layout";

interface CTASectionProps {
  className?: string;
}

export function CTASection({ className }: CTASectionProps) {
  const t = useTranslations('cta');
  const isMobileLandscape = useIsMobileLandscape();
  const router = useRouter();

  return (
    <section id="cta" data-surface="base" className={cn(SECTION_PADDING, "font-outfit bg-site", className)}>
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <div className={cn("bg-site-card text-center px-6 py-14 md:px-14 md:py-16 xl:py-20 xl:px-20 rounded-3xl overflow-hidden")}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-yellow-500 mb-4">
              {t('title')}
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-lg mx-auto">
              {t('subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mb-14">
              <CustomButton onClick={() => router.push("/about/contact")} variant="capsule" size="lg">
                {t('bookService')}
              </CustomButton>
              <CustomButton onClick={() => router.push("/locations")} variant="outline" size="lg" className="text-white border-white hover:bg-yellow-500 hover:text-black hover:border-yellow-500">
                {t('findLocation')}
              </CustomButton>
            </div>
          </div>

          <div className="border-t border-white/10 pt-10 max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-white/70 text-sm">
              <div className="flex flex-col items-center gap-2">
                <Phone className="h-5 w-5 text-yellow-500" />
                <span className="font-medium text-white">{t('callUs')}</span>
                <a
                  href={CONTACT.phones.primary.telHref}
                  data-phone={CONTACT.phones.primary.key}
                  aria-label={`${t('mechanicShopLabel')}: ${CONTACT.phoneFormatted}`}
                  className="hover:text-yellow-500 transition-colors"
                >
                  {t('mechanicShopLabel')}: {CONTACT.phoneFormatted}
                </a>
                <a
                  href={CONTACT.phones.collision.telHref}
                  data-phone={CONTACT.phones.collision.key}
                  aria-label={`${t('collisionLabel')}: ${formatUsPhone(CONTACT.phones.collision.display)}`}
                  className="hover:text-yellow-500 transition-colors"
                >
                  {t('collisionLabel')}: {formatUsPhone(CONTACT.phones.collision.display)}
                </a>
                <a
                  href={CONTACT.phones.parts.telHref}
                  data-phone={CONTACT.phones.parts.key}
                  aria-label={`${t('partsLabel')}: ${formatUsPhone(CONTACT.phones.parts.display)}`}
                  className="hover:text-yellow-500 transition-colors"
                >
                  {t('partsLabel')}: {formatUsPhone(CONTACT.phones.parts.display)}
                </a>
              </div>
              <div className="flex flex-col items-center gap-2">
                <MapPin className="h-5 w-5 text-yellow-500" />
                <span className="font-medium text-white">{t('locations')}</span>
                <span>{t('locationsDetail')}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <span className="font-medium text-white">{t('hours')}</span>
                <span>{t('hoursDetail')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
